import React, { useCallback, useEffect, useState } from 'react';
import SelectMulti from '../../components/select-multi/SelectMulti';
import styles from './profile.module.scss';
import Header from '../../components/header/Header';
import { TextInput, TextInputValue } from '../../components/text-input/TextInput';
import Button from '../../components/button/Button';
import Checkbox from '../../components/checkbox/Checkbox';
import { useUser } from '../../hooks/useUser';
import { gql, useMutation } from '@apollo/client';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useAnalytics, Events, useToast } from '@hooks';
import { planOptions } from '@src/utils/constants';
import { useUserSettings } from '@src/hooks/useUserSettings';
import Divider from '../../components/divider';
import { logger } from '../../utils/logger';

type Props = {};

const MeMutation = gql`
    mutation UpdateMe($input: UserUpdateInput!) {
        updateMe(userInput: $input)
    }
`;

type MeMutationResponse = {
  updateMe: string;
}

function Profile(props: Props) {

  const [update, { loading: loadingUpdate }] = useMutation<MeMutationResponse>(MeMutation);

  const { trackEvent } = useAnalytics();
  const { successToast } = useToast();

  const { setAuthToken } = useAuthToken();
  const [me, { loading }] = useUser(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [usages, setUsages] = useState<string[]>([]);
  const [roleDesc, setRoleDesc] = useState<string>('');
  
  // User settings
  const { autoLogoutEnabled, setAutoLogoutEnabled } = useUserSettings();

  useEffect(() => {
    if (me) {
      me.firstName && setFirstName(me.firstName);
      me.lastName && setLastName(me.lastName);
      if (me.usages) {
        try {
          const parsedUsages = JSON.parse(me.usages);
          // Ensure usages is an array
          setUsages(Array.isArray(parsedUsages) ? parsedUsages : []);
        } catch (error) {
          logger.warn('[Profile] Failed to parse usages JSON:', error);
          setUsages([]);
        }
      }
      me.roleDesc && setRoleDesc(me.roleDesc);

      if (me.autoLogoutEnabled !== undefined && me.autoLogoutEnabled !== null) {
        setAutoLogoutEnabled(me.autoLogoutEnabled);
      }

      const _u = Events.mappers.user(me);
      trackEvent(Events.views.userProfile, _u);
    }
  }, [me, trackEvent, setAutoLogoutEnabled]);

  const handleChangeInput = useCallback((setter) => (value: TextInputValue) => {
    setter(value);
  }, []);

  const handleChangeMultiSelect = useCallback((setter) => (options: string[]) => {
    setter(options);
  }, []);

  const handleChangeText = useCallback((setter) => (value: TextInputValue) => {
    setter(value)
  }, []);

  const save = async () => {
    try {
      if (!firstName || !lastName) {
        return;
      }

      const response = await update({
        variables: {
          input: {
            firstName,
            lastName,
            usages: JSON.stringify(usages), // Convert array to JSON string
            roleDesc,
            autoLogoutEnabled // Include the auto logout setting
          }
        },
      });

      if (response.data?.updateMe) {
        setAuthToken(response.data.updateMe);
        successToast('Profile updated successfully');
      }

      trackEvent(Events.actions.userProfileUpdated, {schoolId: me?.schools?.[0]?.id});
    } catch (e) {
      logger.debug(`[e]`, e);
    }
  };

  return (
    <>
      <Header/>
      <div className={styles.page}>
        <div className={styles.headerTitle}>Profile</div>
        <div className={styles.pageContent}>
          <div className={styles.profileContent}>
            <div className={styles.profileDivider}>
              <Divider label="Profile Section" color="var(--color-dark)" marginTop={20} marginBottom={10} />
            </div>
            
            <div className={styles.row}>
              <TextInput placeholder={'First Name'} label={'First Name:'} id={'firstName'} value={firstName}
                         onChange={handleChangeInput(setFirstName)} disabled={loadingUpdate}/>
              <TextInput placeholder={'Last Name'} label={'Last Name:'} id={'lastName'} value={lastName}
                         onChange={handleChangeInput(setLastName)} disabled={loadingUpdate}/>
            </div>
            <div className={styles.emailWrapper}>
              <span className={styles.email}>Email:&nbsp;</span>{me?.email}
            </div>
            <SelectMulti label={'How do you plan to use Open Parachute? Select all that apply:'} options={planOptions} value={usages}
              onChange={handleChangeMultiSelect(setUsages)} showSearch={false} disabled={loadingUpdate} />
            <TextInput label={'What is your role?'} value={roleDesc}
              onChange={handleChangeText(setRoleDesc)} id="roleDesc" disabled={loadingUpdate} />
            
            <div className={styles.profileDivider}>
              <Divider label="Session Settings" color="var(--color-dark)" marginTop={20} marginBottom={10} />
            </div>
              
            <div className={styles.settingSection}>
              <div className={styles.settingRow}>
                <div className={styles.settingInfo}>
                  <div className={styles.settingLabel}>
                    Auto Logout
                  </div>
                  <p className={styles.settingDescription}>
                    Automatically log out after a period of inactivity. 
                    <br />
                    When disabled, you will remain logged in indefinitely.
                  </p>
                </div>
                
                <div className={styles.settingControl}>
                  <Checkbox
                    id="auto-logout"
                    checked={autoLogoutEnabled}
                    onChange={setAutoLogoutEnabled}
                    disabled={loadingUpdate}
                  />
                </div>
              </div>
            </div>
            
            <Button disabled={loading || loadingUpdate} className={styles.btn} isLoading={loading || loadingUpdate} label={'Save'} onClick={save}/>
          </div>
        </div>
      </div>
    </>
  );
}

export default Profile;
